import axios from 'axios'
import projectAuth from '@/utils/projectAuth'
import auth from '@/utils/auth'

const http = axios.create({ baseURL: '/patrol', timeout: 30000, headers: { 'Content-Type': 'application/json' } })
http.interceptors.request.use(config => {
  config.headers = config.headers || {}
  const token = auth.getToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})
http.interceptors.response.use(res => {
  const body = res.data
  if (body && typeof body.code !== 'undefined' && body.code !== 0) {
    const err = new Error(body.message || '请求失败')
    err.httpStatus = res.status
    err.bizCode = body.code
    return Promise.reject(err)
  }
  return body?.data !== undefined ? body.data : body
}, err => {
  const body = err.response?.data
  const msg = body?.message || err.message
  if (err.response?.status === 401 || body?.code === 401) {
    auth.clearSession()
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname)
    }
    return Promise.reject(new Error(msg || '请先登录'))
  }
  if (err.response?.status === 403 || body?.code === 403) {
    return Promise.reject(new Error(msg || '权限不足'))
  }
  return Promise.reject(err)
})
projectAuth.installProjectAuth(http, (p) => http.post('/auth/context', p).then(r => r))

export const post = (url, data) => http.post(url, data).then(r => r)
export const getAuthContext = (p) => post('/auth/context', p)
export const login = (p) => post('/auth/login', p)
export const logout = () => post('/auth/logout', {})
export const listLoginAccounts = () => post('/auth/accounts', {})
export { projectAuth }

export const SCHEDULE_CYCLE_OPTIONS = [
  { value: 'daily', label: '按天（每天一次）' },
  { value: 'weekly', label: '按周（每周一次）' },
  { value: 'biweekly', label: '按两周（每两周一次）' },
  { value: 'monthly', label: '按月（每月一次）' },
  { value: 'duty_shift', label: '值守班次（固定日期 + 日/晚班）' }
]

export const DEFAULT_SHIFT_SLOTS = [
  { code: 'day', label: '日班', startTime: '10:00', endTime: '18:00', weekdays: [1, 2, 3, 4, 5, 6, 7] },
  { code: 'evening', label: '晚班', startTime: '18:00', endTime: '00:00', weekdays: [1, 2, 3, 4, 5, 6, 7] }
]

export const WEEKDAY_OPTIONS = [
  { value: 1, label: '一' }, { value: 2, label: '二' }, { value: 3, label: '三' },
  { value: 4, label: '四' }, { value: 5, label: '五' }, { value: 6, label: '六' }, { value: 7, label: '日' }
]

export const SHIFT_SCOPE_OPTIONS = [
  { value: 'all', label: '共用' },
  { value: 'day', label: '日班' },
  { value: 'evening', label: '晚班' }
]

export function shiftScopeOptionsForPlan(plan) {
  const slots = plan?.shiftSlots?.length ? plan.shiftSlots : DEFAULT_SHIFT_SLOTS
  return [
    { value: 'all', label: '共用' },
    ...slots.map(s => ({ value: s.code, label: s.label || s.code }))
  ]
}

export function planDateWeekday(planDate) {
  const cur = new Date(String(planDate).slice(0, 10) + 'T12:00:00')
  const dow = cur.getDay()
  return dow === 0 ? 7 : dow
}

export function slotAppliesOnDate(slot, planDate) {
  const wds = slot?.weekdays?.length ? slot.weekdays : [1, 2, 3, 4, 5, 6, 7]
  return wds.includes(planDateWeekday(planDate))
}

export function slotTimeText(slot) {
  if (!slot?.startTime) return ''
  const end = slot.endTime === '00:00' ? '00:00' : (slot.endTime || '')
  return end ? `${slot.startTime}–${end}` : slot.startTime
}

export const scheduleCycleLabel = (cycle) =>
  SCHEDULE_CYCLE_OPTIONS.find(o => o.value === cycle)?.label?.replace(/（.+）/, '') || '按天'

export const getTemplateList = (p) => post('/patrol-template/get-template-list', p || {})
export const saveTemplate = (p) => post('/patrol-template/add-update', p)
export const releaseTemplate = (p) => post('/patrol-template/release-template', p)
export const disableTemplate = (p) => post('/patrol-template/disable-template', p)
export const copyTemplate = (p) => post('/patrol-template/copy-template', p)
export const bindTemplateProjects = (p) => post('/patrol-template/bind-projects', p)
export const getTemplateDetail = (p) => post('/patrol-template/get-template-detail', p)
export const deleteTemplate = (p) => post('/patrol-template/delete-template', p)
export const saveCatalogue = (p) => post('/catalogue/save-catalogue', p)
export const deleteCatalogue = (p) => post('/catalogue/delete-catalogue', p)
export const saveQuestion = (p) => post('/question/save-question', p)
export const deleteQuestion = (p) => post('/question/delete-question', p)
export const listProjects = () => post('/user/search-leasing-project', {})
export const listProjectManage = (p) => post('/project/list', p || {})
export const saveProject = (p) => post('/project/save', p)
export const deleteProject = (p) => post('/project/delete', p)

export const listTags = () => post('/tag/get-tag-page', {}).then(r => r.list || r)
export const saveTag = (p) => post('/tag/add-question-tag', p)
export const deleteTag = (p) => post('/tag/delete-tag', p)
export const saveTagQuestionRel = (p) => post('/tag/save-tag-question-rel', p)

export const listDatasources = () => post('/datasource/get-datasource-list', {}).then(r => r.list || r)
export const saveDatasource = (p) => post('/datasource/add-datasource', p)
export const deleteDatasource = (p) => post('/datasource/delete-datasource', p)
export const listDepartments = () => post('/datasource/get-patrol-department', {})

export const getWarningConfig = () => post('/warning/get-config', {})
export const saveWarningConfig = (p) => post('/warning/save-config', p)
export const getNotificationSettings = () => post('/notification/settings', {})
export const saveNotificationChannels = (p) => post('/notification/save-channels', p)
export const saveNotificationRules = (p) => post('/notification/save-rules', p)

export const getAiConfig = () => post('/ai-config/get', {})
export const saveAiConfigConnection = (p) => post('/ai-config/save-connection', p)
export const saveAiConfigModels = (p) => post('/ai-config/save-models', p)
export const saveAiConfigBehavior = (p) => post('/ai-config/save-behavior', p)
export const listAiModels = () => post('/ai-config/list-models', {})
export const testAiConnection = () => post('/ai-config/test', {})

export const getVisionStats = (p) => post('/vision/stats', p)
export const getCaseLibraryStats = () => post('/vision/cases/stats', {})
export const listVisionSessions = (p) => post('/vision/sessions/list', p)
export const getVisionSession = (sessionId) => post('/vision/sessions/get', { sessionId })
export const previewVisionImport = (p) => post('/vision/cases/import/preview', p)
export const executeVisionImport = (p) => post('/vision/cases/import/execute', p)

export const getAssistantRules = () => post('/assistant-rules/get', {})
export const saveAssistantRules = (p) => post('/assistant-rules/save', p)
export const resetAssistantRules = () => post('/assistant-rules/reset', {})

// 主数据
export const listMasterTypes = () => post('/master-data/types', {})
export const listMasterData = (p) => post('/master-data/list', p)
export const saveMasterData = (p) => post('/master-data/save', p)
export const deleteMasterData = (p) => post('/master-data/delete', p)
export const syncLeaseUnitsFromBdc = () => post('/master-data/sync-bdc', {})

// 定时任务
export const listScheduledTasks = () => post('/scheduled-task/list', {})
export const listScheduledActionTypes = () => post('/scheduled-task/action-types', {})
export const saveScheduledTask = (p) => post('/scheduled-task/save', p)
export const deleteScheduledTask = (p) => post('/scheduled-task/delete', p)
export const runScheduledTask = (p) => post('/scheduled-task/run', p)
export const listScheduledTaskLogs = (p) => post('/scheduled-task/logs', p || {})

export const listStaff = (p) => post('/staff/list', p || {})
export const getStaffAuth = (p) => post('/staff/get-auth', p)
export const saveStaff = (p) => post('/staff/save', p)
export const syncBdcStaff = () => post('/staff/sync-bdc', {})
export const listRoles = (p) => post('/role/list', p || {})
export const saveRole = (p) => post('/role/save', p)
export const setRoleStatus = (p) => post('/role/set-status', p)
export const listPermissions = () => post('/role/permissions', {})
export const getStaffRoles = (p) => post('/role/staff-roles', p)
export const setStaffRoles = (p) => post('/role/set-staff-roles', p)

export const listSchedules = (p) => post('/workforce-management/list', p || {})
export const assignSchedule = (p) => post('/workforce-management/assign', p)
export const batchAssignSchedule = (p) => post('/workforce-management/batch-assign', p)
export const deleteSchedule = (p) => post('/workforce-management/delete-workforce', p)
export const getMonthSchedule = (p) => post('/workforce-management/get-month-back-list', p)
export const saveDaySchedule = (p) => post('/workforce-management/save-day-schedule', p)
export const saveDutyBatchSchedule = (p) => post('/workforce-management/save-duty-batch-schedule', p)
export const createGrabSlot = (p) => post('/workforce-management/create-grab-slot', p)
export const listGrabPool = (p) => post('/workforce-management/grab-pool-list', p)
export const claimGrabTask = (p) => post('/workforce-management/claim-task', p)
export const releaseGrabTask = (p) => post('/workforce-management/release-task', p)
export const manageGrabSchedule = (p) => post('/workforce-management/manage-grab-schedule', p)
export const getGrabConfig = (p) => post('/project/grab-config/get', p || {})
export const saveGrabConfig = (p) => post('/project/grab-config/save', p)
export const importWorkforce = (rows) => post('/workforce-management/workforce-import', { rows })
export const arrangeMerchant = (p) => post('/workforce-management/arrange', p)
export const getMerchantAssign = (p) => post('/workforce-management/get-merchant-assign', p)
export const changeShift = (p) => post('/workforce-management/change', p)
export const listMerchants = () => post('/merchant/list', {})
export const saveSpecialSurvey = (p) => post('/special-survey/save', p)
export const listSpecialSurveys = () => post('/special-survey/list', {})

export const getReportList = (p) => post('/patrol-report/get-manage-patrol-report-list', p || {})
export const getReportDetail = (p) => post('/patrol-report/get-detail', p)
export const exportReports = () => post('/patrol-report/export', {})
export const deleteReport = (p) => post('/patrol-report/delete-patrol-report', p)

export const getIssueList = (p) => post('/question-user-record/get-question-user-record', p || {})
export const getIssueCount = () => post('/question-user-record/get-question-user-record-count', {})
export const handleIssue = (p) => post('/question-user-record/handle-question', p)
export const getIssueDetail = (p) => post('/question-user-record/get-question-user-record-detail', p)
export const uploadFile = (p) => post('/file/upload', p)
export const updateIssueDepartment = (p) => post('/question-user-record/update-department', p)
export const updateInfrastructure = (p) => post('/question-user-record/update-infrastructure', p)
export const exportIssues = () => post('/question-user-record/export', {})

// 问题库
export const listQuestionBank = (p) => post('/question-bank/list', p || {})
export const saveQuestionBank = (p) => post('/question-bank/save', p)
export const deleteQuestionBank = (p) => post('/question-bank/delete', p)
export const addBankQuestionsToTemplate = (p) => post('/question-bank/add-to-template', p)

export const getWorkbenchStatus = (p) => post('/workbench/status', p || {})
export const getAssistantHealth = (p) => post('/assistant/health', p || {})
export const getAssistantConfig = () => post('/assistant/config', {})
export const assistantChat = (p) => post('/assistant/chat', p || {})
export const executeAssistantAction = (p) => post('/assistant/execute', p || {})
export const listNotifications = (p) => post('/notifications/list', p || {})
export const markNotificationsRead = (p) => post('/notifications/read', p || {})

export default { post }
